<script lang="ts">
    import { _ } from 'svelte-i18n';
    import { z } from 'zod';

    import { RouteType } from './RouteType';
    import { RouteTypeListApp } from './RouteTypeListApp';
    import VehicleOneLabel from '../Vehicles/VehicleOneLabel.svelte';
    import SearchForm from '../../shared/SearchForm.svelte';
    import VehicleOneParagraph from '../Vehicles/VehicleOneParagraph.svelte';
    import VehicleOneSection from '../Vehicles/VehicleOneSection.svelte';
    import VehicleOneValue from '../Vehicles/VehicleOneValue.svelte';
    import Checkbox from '../../shared/Checkbox/Checkbox.svelte';
    import InputText from '../../shared/InputText.svelte';
    import Select from '../../shared/Select.svelte';
    import InputNumber from '../../shared/InputNumber.svelte';
    import { debug } from '../../../stores/store';
    import Switch from '../../shared/Switch.svelte';
    import { RouteTypeValidator } from './RouteTypeValidator';
    import ErrorBounduary from '../Location/ErrorBounduary.svelte';
    import TemplateSettingsGeneral from '../Settings/TemplateSettingsGeneral.svelte';
    import { features } from '../../shared/Features/features';
    import { canWrite } from '../../shared/Permissions/writeAccess';

    export let routeType: RouteType;
    export let onChange: (routeType: RouteType) => void;

    let rtv = new RouteTypeValidator();

    /** Konto bez can_write ogląda typ trasy - backend i tak odrzuci PATCH z 403 read_only. */
    $: writeBlocked = !$canWrite;

    // // {"type": "optimal", "vehicle": {"load": 5, "width": 2.5, "height": 3, "length": 10, "weight": 12, "category": [1, 2, 3, 4], "euro_norm": [5]}}

    $: rtv = rtv;
</script>

{#if $debug}
    <div>{routeType.getName()}</div>
    <div>{JSON.stringify(routeType.getAttributes())}</div>
{/if}

<TemplateSettingsGeneral>
    <!--    <div slot="label">{$_('Czy aktywna')}</div>-->
    <div slot="content">
        <div style="display: none">
            <!-- temporarily hide activity switch -->
            <VehicleOneParagraph>
                <div slot="title">{$_('Active')}</div>
                <div slot="content">
                    <VehicleOneSection>
                        <VehicleOneLabel>
                            <Switch
                                disabled={true}
                                checked={routeType.isActive()}
                                on:change={v => {
                                    routeType = routeType.setActive(v.detail.checked);
                                    onChange(routeType);
                                }}
                            />
                        </VehicleOneLabel>
                    </VehicleOneSection>
                </div>
            </VehicleOneParagraph>
        </div>
        <VehicleOneParagraph>
            <div slot="title">{$_('Route')}</div>
            <div slot="content">
                <VehicleOneSection>
                    <VehicleOneLabel>
                        {$_('Name')}
                    </VehicleOneLabel>
                    <VehicleOneValue>
                        <InputText
                            disabled={writeBlocked}
                            value={routeType.getName()}
                            on:change={e => {
                                routeType = routeType.setName(e.target?.value ?? '');
                                onChange(routeType);
                            }}
                        />
                    </VehicleOneValue>
                </VehicleOneSection>
                <VehicleOneSection>
                    <VehicleOneLabel>
                        {$_('Route calculation type')}
                    </VehicleOneLabel>
                    <VehicleOneValue>
                        <!-- https://docs.automapa.pl/wiki/internal/admin/Targeo/48/8/13/ -->
                        <!-- https://er.automapa.pl/issues/42527 -->
                        <Select
                            disabled={writeBlocked}
                            selected={routeType.getRouteCalcType()}
                            items={[
                                { id: 'optimal', label: $_('Optimal') },
                                { id: 'quick', label: $_('Fastest') },
                                { id: 'short', label: $_('Shortest') },
                                { id: 'easy', label: $_('Easy') },
                                { id: 'soft', label: $_('Safe') },
                            ]}
                            onChange={s => {
                                console.log('Selected', s);
                                routeType = routeType.setRouteCalcType(s);
                                onChange(routeType);
                                return true;
                            }}
                        />
                    </VehicleOneValue>
                </VehicleOneSection>
                <VehicleOneSection>
                    <VehicleOneLabel>
                        {$_('Departure time')}
                    </VehicleOneLabel>
                    <VehicleOneValue>
                        <InputText
                            disabled={writeBlocked}
                            value={routeType.getAttributes().start_time}
                            on:change={e => {
                                routeType = routeType.setStartTime(e.target?.value ?? '');
                                onChange(routeType);
                            }}
                        />
                    </VehicleOneValue>
                </VehicleOneSection>
            </div>
        </VehicleOneParagraph>
        <ErrorBounduary errors={rtv.getErrors()} valid={rtv.isAllValid()} withPadding={true}>
            <VehicleOneParagraph>
                <div slot="title">{$_('Vehicle')}</div>
                <div slot="content">
                    <VehicleOneSection>
                        <VehicleOneLabel>
                            {$_('Vehicle categories')}
                        </VehicleOneLabel>
                        <VehicleOneValue>
                            <InputText disabled={true} value={routeType.getAttributes().vehicle.category.join(',')} />
                        </VehicleOneValue>
                    </VehicleOneSection>
                    <VehicleOneSection>
                        <VehicleOneLabel>
                            {$_('Euro norm')}
                        </VehicleOneLabel>
                        <VehicleOneValue>
                            <Select
                                disabled={$features?.feature_enable_change_euro_norm === false ||
                                    writeBlocked}
                                selected={routeType.getAttributes().vehicle.euro_norm[0].toString()}
                                items={[
                                    { id: '1', label: '1' },
                                    { id: '2', label: '2' },
                                    { id: '3', label: '3' },
                                    { id: '4', label: '4' },
                                    { id: '5', label: '5' },
                                ]}
                                onChange={s => {
                                    console.log('Selected', s);
                                    routeType = routeType.setEuroNorm(parseInt(s));
                                    onChange(routeType);
                                    return true;
                                }}
                            />
                            <!--                            <InputText disabled={true} value={routeType.getAttributes().vehicle.euro_norm.join(',')} />-->
                        </VehicleOneValue>
                    </VehicleOneSection>
                    <VehicleOneSection>
                        <VehicleOneLabel>
                            {$_('Vehicle length [m]')}
                        </VehicleOneLabel>
                        <VehicleOneValue>
                            <Select
                                disabled={writeBlocked}
                                selected={routeType.getAttributes().vehicle.length.toString()}
                                items={[
                                    { id: '4', label: '4' },
                                    { id: '6', label: '6' },
                                    { id: '8', label: '8' },
                                    { id: '10', label: '10' },
                                    { id: '12', label: '12' },
                                    { id: '14', label: '14' },
                                    { id: '16', label: '16' },
                                    { id: '18', label: '18' },
                                ]}
                                onChange={s => {
                                    console.log('Selected', s);
                                    routeType = routeType.setVehicleLength(parseFloat(s));
                                    onChange(routeType);
                                    return true;
                                }}
                            />
                            <!--                        <InputNumber-->
                            <!--                            disabled={false}-->
                            <!--                            value={routeType.getAttributes().vehicle.length.toString()}-->
                            <!--                            valid={z.coerce-->
                            <!--                                .number()-->
                            <!--                                .safeParse(routeType.getAttributes().vehicle.length).success ===-->
                            <!--                                true}-->
                            <!--                            on:change={e => {-->
                            <!--                                console.log('Changed', e);-->
                            <!--                                routeType = routeType.setVehicleLength(parseFloat(e.target?.value));-->
                            <!--                                onChange(routeType);-->
                            <!--                            }}-->
                            <!--                        />-->
                        </VehicleOneValue>
                    </VehicleOneSection>
                    <VehicleOneSection>
                        <VehicleOneLabel>
                            {$_('Vehicle width [m]')}
                        </VehicleOneLabel>
                        <VehicleOneValue>
                            <Select
                                disabled={writeBlocked}
                                selected={routeType.getAttributes().vehicle.width.toString()}
                                items={[
                                    { id: '2.0', label: '2.0' },
                                    { id: '2.1', label: '2.1' },
                                    { id: '2.2', label: '2.2' },
                                    { id: '2.3', label: '2.3' },
                                    { id: '2.4', label: '2.4' },
                                    { id: '2.5', label: '2.5' },
                                    { id: '2.6', label: '2.6' },
                                ]}
                                onChange={s => {
                                    console.log('Selected', s);
                                    routeType = routeType.setVehicleWidth(parseFloat(s));
                                    onChange(routeType);
                                    return true;
                                }}
                            />
                            <!--                        <InputNumber-->
                            <!--                            disabled={false}-->
                            <!--                            value={routeType.getAttributes().vehicle.width.toString()}-->
                            <!--                            valid={z.coerce-->
                            <!--                                .number()-->
                            <!--                                .safeParse(routeType.getAttributes().vehicle.width).success ===-->
                            <!--                                true}-->
                            <!--                            on:change={e => {-->
                            <!--                                console.log('Changed', e);-->
                            <!--                                routeType = routeType.setVehicleWidth(parseFloat(e.target?.value));-->
                            <!--                                onChange(routeType);-->
                            <!--                            }}-->
                            <!--                        />-->
                        </VehicleOneValue>
                    </VehicleOneSection>
                    <VehicleOneSection>
                        <VehicleOneLabel>
                            {$_('Vehicle height [m]')}
                        </VehicleOneLabel>
                        <VehicleOneValue>
                            <Select
                                disabled={writeBlocked}
                                selected={routeType.getAttributes().vehicle.height.toString()}
                                items={[
                                    { id: '3.0', label: '3.0' },
                                    { id: '3.1', label: '3.1' },
                                    { id: '3.2', label: '3.2' },
                                    { id: '3.3', label: '3.3' },
                                    { id: '3.4', label: '3.4' },
                                    { id: '3.5', label: '3.5' },
                                    { id: '3.6', label: '3.6' },
                                    { id: '3.7', label: '3.7' },
                                    { id: '3.8', label: '3.8' },
                                    { id: '3.9', label: '3.9' },
                                    { id: '4.0', label: '4.0' },
                                    { id: '4.1', label: '4.1' },
                                ]}
                                onChange={s => {
                                    console.log('Selected', s);
                                    routeType = routeType.setVehicleHeight(parseFloat(s));
                                    onChange(routeType);
                                    return true;
                                }}
                            />
                            <!--                        <InputNumber-->
                            <!--                            disabled={false}-->
                            <!--                            value={routeType.getAttributes().vehicle.height.toString()}-->
                            <!--                            valid={z.coerce-->
                            <!--                                .number()-->
                            <!--                                .safeParse(routeType.getAttributes().vehicle.height).success ===-->
                            <!--                                true}-->
                            <!--                            on:change={e => {-->
                            <!--                                console.log('Changed', e);-->
                            <!--                                routeType = routeType.setVehicleHeight(parseFloat(e.target?.value));-->
                            <!--                                onChange(routeType);-->
                            <!--                            }}-->
                            <!--                        />-->
                        </VehicleOneValue>
                    </VehicleOneSection>
                    <VehicleOneSection>
                        <VehicleOneLabel>
                            {$_('Axle load [t]')}
                        </VehicleOneLabel>
                        <VehicleOneValue>
                            <Select
                                disabled={writeBlocked}
                                selected={routeType.getAttributes().vehicle.load.toString()}
                                items={[
                                    { id: '1', label: '1' },
                                    { id: '2', label: '2' },
                                    { id: '3', label: '3' },
                                    { id: '4', label: '4' },
                                    { id: '5', label: '5' },
                                    { id: '6', label: '6' },
                                    { id: '7', label: '7' },
                                    { id: '8', label: '8' },
                                    { id: '9', label: '9' },
                                    { id: '10', label: '10' },
                                    { id: '11', label: '11' },
                                    { id: '11.5', label: '11.5' },
                                ]}
                                onChange={s => {
                                    console.log('Selected', s);
                                    routeType = routeType.setVehicleLoad(parseFloat(s));
                                    onChange(routeType);
                                    return true;
                                }}
                            />
                            <!--                        <InputNumber-->
                            <!--                            disabled={false}-->
                            <!--                            value={routeType.getAttributes().vehicle.load.toString()}-->
                            <!--                            check={v => {-->
                            <!--                                return rtv.checkVehicleLoad(v).length === 0;-->
                            <!--                            }}-->
                            <!--                            on:change={e => {-->
                            <!--                                rtv = rtv;-->
                            <!--                                console.log('Changed', e);-->
                            <!--                                let v = e.target?.value;-->
                            <!--                                if (rtv.checkVehicleLoad(v).length === 0) {-->
                            <!--                                    routeType = routeType.setVehicleLoad(parseFloat(v));-->
                            <!--                                    onChange(routeType);-->
                            <!--                                }-->
                            <!--                            }}-->
                            <!--                        />-->
                        </VehicleOneValue>
                    </VehicleOneSection>
                    <VehicleOneSection>
                        <VehicleOneLabel>
                            {$_('DMC [t]')}
                        </VehicleOneLabel>
                        <VehicleOneValue>
                            <Select
                                disabled={writeBlocked}
                                selected={routeType.getAttributes().vehicle.weight.toString()}
                                items={[
                                    // https://spedytor.org/dmc-i-nacisk-na-osie-wyjasniamy/
                                    { id: '3.5', label: '3.5' },
                                    { id: '12', label: '12' },
                                    { id: '18', label: '18' },
                                    { id: '24', label: '24' },
                                    { id: '36', label: '36' },
                                    { id: '40', label: '40' },
                                ]}
                                onChange={s => {
                                    console.log('Selected', s);
                                    routeType = routeType.setVehicleWeight(parseFloat(s));
                                    onChange(routeType);
                                    return true;
                                }}
                            />

                            <!--                        <InputNumber-->
                            <!--                            disabled={false}-->
                            <!--                            value={routeType.getAttributes().vehicle.weight.toString()}-->
                            <!--                            valid={z.coerce-->
                            <!--                                .number()-->
                            <!--                                .safeParse(routeType.getAttributes().vehicle.weight).success ===-->
                            <!--                                true}-->
                            <!--                            on:change={e => {-->
                            <!--                                console.log('Changed', e);-->
                            <!--                                routeType = routeType.setVehicleWeight(parseFloat(e.target?.value));-->
                            <!--                                onChange(routeType);-->
                            <!--                            }}-->
                            <!--                        />-->
                        </VehicleOneValue>
                    </VehicleOneSection>
                </div>
            </VehicleOneParagraph>
        </ErrorBounduary>
    </div></TemplateSettingsGeneral
>

<style>
    .vehicle-expanded-container {
        background-color: var(--selected-item-background);
        /*background-color: #f92672;*/
    }
</style>
